import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Message } from '../types';
import IntentRouterPanel from './IntentRouterPanel';
import { fetchMessageAudio } from '../services/api';
import {
  AutoAwesomeRounded,
  DownloadRounded,
  ContentCopyRounded,
  CheckRounded,
  ZoomInRounded,
  CloseRounded,
  AddRounded,
  RemoveRounded,
  RestartAltRounded,
  VolumeUpRounded,
  StopRounded,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import mermaid from 'mermaid';
import 'katex/dist/katex.min.css';
import { sanitizeMath } from '../utils/mathText';

// ---------------------------------------------------------------------------
// Download helpers
// ---------------------------------------------------------------------------

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so the download has time to start
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Slugify a label for a filename. Keeps alphanumerics + dashes.
function slugForFilename(seed: string, max = 40): string {
  const slug = seed
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, max);
  return slug || 'diagram';
}

// Save the SVG string verbatim as a .svg file.
function downloadSvg(svg: string, baseName: string) {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  triggerDownload(blob, `${baseName}.svg`);
}

/** True when a blob looks like an SVG (XML text starting with <?xml or <svg). */
function isSvgBlob(blob: Blob): boolean {
  const type = (blob.type || '').toLowerCase();
  return type.startsWith('image/svg') || type === 'application/svg+xml';
}

/**
 * Pull a sensible numeric (width, height) from an SVG string.
 * Priority: viewBox > numeric width/height attrs > sensible default.
 * This avoids the trap where `width="100%"` parses to "1" and gives us
 * a 1×1 canvas.
 */
function svgIntrinsicSize(svgText: string): { width: number; height: number } {
  const FALLBACK = 1024;
  // viewBox is the canonical aspect spec. Try it first.
  const vbMatch = /\bviewBox\s*=\s*["']([^"']+)["']/i.exec(svgText);
  if (vbMatch) {
    const parts = vbMatch[1].trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every((n) => Number.isFinite(n)) && parts[2] > 0 && parts[3] > 0) {
      return { width: parts[2], height: parts[3] };
    }
  }
  // Fall back to width/height — but only if numeric (skip '100%' etc.).
  const wMatch = /\bwidth\s*=\s*["']?(\d+(?:\.\d+)?)(?:px)?["'\s>]/i.exec(svgText);
  const hMatch = /\bheight\s*=\s*["']?(\d+(?:\.\d+)?)(?:px)?["'\s>]/i.exec(svgText);
  const w = wMatch && parseFloat(wMatch[1]);
  const h = hMatch && parseFloat(hMatch[1]);
  return {
    width: w && w > 1 ? w : FALLBACK,
    height: h && h > 1 ? h : FALLBACK,
  };
}

/**
 * Rasterize an SVG (as text) to a PNG blob via an off-screen canvas.
 *
 * Handles the gotchas that have bitten this before:
 *  - width="100%" in the SVG → use viewBox instead.
 *  - `<img>` waits for size resolution before onload fires → set decoding
 *    + width/height to force a sized load.
 *  - Some browsers taint the canvas if the SVG was loaded without
 *    crossOrigin set → set crossOrigin='anonymous' even for blob: URLs.
 */
async function rasterizeSvgToPng(svgText: string): Promise<Blob> {
  const { width, height } = svgIntrinsicSize(svgText);
  const scale = 2;  // retina-sharp

  // Inline an explicit width/height on the root <svg> if missing, so the
  // <img> below resolves an unambiguous natural size before painting.
  let armoredSvg = svgText;
  if (!/<svg[^>]*\bwidth\s*=\s*["']\d+/i.test(armoredSvg)) {
    armoredSvg = armoredSvg.replace(/<svg\b/i, `<svg width="${width}" height="${height}"`);
  }

  const svgBlob = new Blob([armoredSvg], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.width = Math.round(width);
    img.height = Math.round(height);

    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('img.onload timeout (10s)')), 10000);
      img.onload = () => { clearTimeout(t); resolve(); };
      img.onerror = () => { clearTimeout(t); reject(new Error('img.onerror fired (SVG failed to load)')); };
      img.src = svgUrl;
    });
    // Force a decode pass too — surfaces parse errors `onload` may swallow.
    if (img.decode) {
      try { await img.decode(); } catch { /* some browsers throw on SVG decode; harmless if onload already fired */ }
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.fillStyle = '#ffffff';   // white bg so it pastes cleanly into docs
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return await new Promise<Blob>((resolve, reject) => {
      try {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
          'image/png',
        );
      } catch (e) {
        // Common one: SecurityError: Tainted canvases may not be exported.
        reject(e);
      }
    });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

/**
 * Resolve the download source to a Blob without a network call when we
 * already have the bytes. Prefer this over `fetch(url)` because:
 *
 *   - `<img>` can DISPLAY a cross-origin URL without CORS, but `fetch()`
 *     to the same URL REQUIRES it. Re-fetching for download trips CORS
 *     in dev when the frontend runs on 127.0.0.1 instead of localhost,
 *     or on a port the backend doesn't allow.
 *   - Blob: URLs can be revoked between display and click. A cached
 *     Blob in component memory survives that.
 *   - It's free — the bytes are already in IDB / RAM.
 *
 * The component passes its `cachedBlobRef.current` first; if null, we
 * fall back to fetching (which still works for first-render same-origin
 * cases and properly-CORS'd cross-origin cases).
 */
async function sourceToBlob(source: Blob | string): Promise<Blob> {
  if (source instanceof Blob) return source;
  const resp = await fetch(source);
  if (!resp.ok) throw new Error(`fetch ${source} → HTTP ${resp.status}`);
  return resp.blob();
}

/**
 * Download an image as a .png file.
 *
 * Robust to both source formats:
 *   - If the source is a PNG → save the bytes directly.
 *   - If the source is an SVG → rasterize to PNG via canvas first.
 *     If rasterization fails (e.g. canvas tainted, browser quirk),
 *     fall back to saving the SVG bytes — the kid still gets a file.
 *
 * `source` is either a Blob (preferred — already in memory) or a URL
 * string (fallback — will hit the network and may trip CORS).
 */
async function downloadImageAsPng(source: Blob | string, baseName: string): Promise<void> {
  try {
    const blob = await sourceToBlob(source);
    if (!isSvgBlob(blob)) {
      triggerDownload(blob, `${baseName}.png`);
      return;
    }

    // SVG source → try to rasterize. On failure, fall back to SVG bytes
    // so the click still produces a usable file.
    const svgText = await blob.text();
    try {
      const pngBlob = await rasterizeSvgToPng(svgText);
      triggerDownload(pngBlob, `${baseName}.png`);
    } catch (rasterErr) {
      // eslint-disable-next-line no-console
      console.warn(
        '[downloadImageAsPng] rasterize failed, saving SVG instead:',
        rasterErr,
      );
      const fallback = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      triggerDownload(fallback, `${baseName}.svg`);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[downloadImageAsPng]', err);
    throw err;
  }
}

/**
 * Download a raster image (from a URL) as an .svg file with the PNG embedded
 * inside an <image> tag. The result is a real SVG file that opens in any
 * vector tool (Illustrator, Figma, Inkscape) and scales losslessly via the
 * SVG container. The embedded PNG is still raster (so zooming past its
 * native resolution pixelates), but for kid use-cases — pasting into a doc,
 * resizing without quality loss within the original resolution — this is
 * usually what they want.
 *
 * Reads PNG width/height from the IHDR chunk (bytes 16-23 after the 8-byte
 * signature) so the wrapper sizes the embedded image correctly.
 */
async function downloadImageAsSvg(source: Blob | string, baseName: string): Promise<void> {
  let blob: Blob;
  try {
    blob = await sourceToBlob(source);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[downloadImageAsSvg]', err);
    throw err;
  }

  // Fast path: the source IS already SVG → save the bytes directly.
  // No need to read PNG headers or wrap anything.
  if (isSvgBlob(blob)) {
    const text = await blob.text();
    const out = new Blob([text], { type: 'image/svg+xml;charset=utf-8' });
    triggerDownload(out, `${baseName}.svg`);
    return;
  }

  // Slow path: source is a raster (PNG). Wrap it in an SVG <image> element
  // so the downloaded file is a real .svg (opens in Illustrator/Figma,
  // scales losslessly via the SVG container; embedded raster underneath).
  const buf = new Uint8Array(await blob.arrayBuffer());

  // Pull intrinsic size from the PNG IHDR chunk. PNG header is at byte 8;
  // width at 16-19, height at 20-23 (big-endian uint32).
  // Parens grouping each shift to silence the no-mixed-operators rule.
  let width = 1024;
  let height = 1024;
  if (buf.length >= 24 && buf[0] === 0x89 && buf[1] === 0x50) {
    width  = (((buf[16] << 24) | (buf[17] << 16)) | ((buf[18] << 8) | buf[19])) >>> 0;
    height = (((buf[20] << 24) | (buf[21] << 16)) | ((buf[22] << 8) | buf[23])) >>> 0;
  }

  // Base64-encode the PNG bytes for embedding as a data URI.
  let bin = '';
  // Chunked encode to avoid call-stack-overflow on large arrays.
  const CHUNK = 0x8000;
  for (let i = 0; i < buf.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, Array.from(buf.subarray(i, i + CHUNK)));
  }
  const b64 = btoa(bin);

  const wrappedSvg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <image width="${width}" height="${height}" preserveAspectRatio="xMidYMid meet" href="data:image/png;base64,${b64}" />
</svg>
`;
  const wrappedBlob = new Blob([wrappedSvg], { type: 'image/svg+xml;charset=utf-8' });
  triggerDownload(wrappedBlob, `${baseName}.svg`);
}

// Rasterize the SVG to a PNG via an offscreen <canvas>. Uses the SVG's own
// width/height with a 2x scale so the PNG is crisp on retina displays.
async function downloadPng(svg: string, baseName: string) {
  // Parse the SVG to read its size; fall back to 800×600 if unavailable.
  const parser = new DOMParser();
  const doc = parser.parseFromString(svg, 'image/svg+xml');
  const svgEl = doc.documentElement;
  const widthAttr = parseFloat(svgEl.getAttribute('width') || '0');
  const heightAttr = parseFloat(svgEl.getAttribute('height') || '0');
  const viewBox = (svgEl.getAttribute('viewBox') || '').split(/\s+/).map(Number);
  const w = widthAttr || (viewBox[2] ?? 800);
  const h = heightAttr || (viewBox[3] ?? 600);
  const scale = 2;

  const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = svgUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = w * scale;
  canvas.height = h * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    URL.revokeObjectURL(svgUrl);
    throw new Error('Canvas 2D context unavailable');
  }
  // White background so PNG isn't transparent (better for pasting into Word).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(svgUrl);

  await new Promise<void>((resolve) => {
    canvas.toBlob((blob) => {
      if (blob) triggerDownload(blob, `${baseName}.png`);
      resolve();
    }, 'image/png');
  });
}

// ---------------------------------------------------------------------------
// IndexedDB cache for generated images
// ---------------------------------------------------------------------------
// Treats the local browser as the durable file store for images. Once an
// image is downloaded the first time we drop the raw bytes into IndexedDB
// keyed by URL. Every subsequent page open (or revisit, or even after the
// browser HTTP cache is cleared) we serve from IDB directly — zero network
// requests, instant render. Only `Clear site data` or `Clear IndexedDB`
// wipes it.
//
// Survives:
//   * DevTools "Disable cache" toggle
//   * Browser cache eviction on disk-pressure
//   * Private-mode session ending (only this session's data is lost)
// Doesn't survive:
//   * Clearing site data in browser settings
//   * Manually deleting the IndexedDB
//
// Failure tolerant: if IDB is unavailable (e.g. iOS private mode), all
// helpers no-op and the component falls back to network fetches.

const IDB_NAME = 'curiosity-coach-images';
const IDB_STORE = 'images';
const IDB_VERSION = 1;

let _idbPromise: Promise<IDBDatabase> | null = null;

function openImageDB(): Promise<IDBDatabase> {
  if (_idbPromise) return _idbPromise;
  _idbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _idbPromise.catch((err) => {
    _idbPromise = null;  // allow retry on next call
    throw err;
  });
}

async function getCachedImageBlob(url: string): Promise<Blob | null> {
  try {
    const db = await openImageDB();
    return await new Promise<Blob | null>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(url);
      req.onsuccess = () => resolve((req.result as Blob | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function putCachedImageBlob(url: string, blob: Blob): Promise<void> {
  try {
    const db = await openImageDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(blob, url);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* swallow — caching is best-effort */
  }
}

// ---------------------------------------------------------------------------
// Coach emphasis — bold the closing block + a leading praise + "wow" markers
// ---------------------------------------------------------------------------
// Applied ONLY to the coach's replies (never the kid's own messages). Jobs:
//   1. Bold the whole CLOSING BLOCK the reply ends on — not just one question.
//      If the reply ends with its own paragraph (e.g. "Here's something to
//      ponder: …" + one or two questions), the entire paragraph is bolded so
//      the kid's eye lands on the thing they're meant to think about.
//   2. Bold a LEADING PRAISE interjection ("Exactly!", "Whoa, nice catch!", …)
//      — a short opening clause that ends in "!".
//   3. Bold a small set of "wow"/interest phrases ("wow", "interesting fact",
//      "did you know", …) in the body so the delight beats pop.
// Deterministic — runs on every render regardless of what the model emitted,
// and works on already-saved messages too.

// "wow"/interest markers (longest phrases first so alternation prefers them).
const COACH_MARKERS = [
  'interesting fact',
  'fun fact',
  "here'?s the cool part",
  'did you know',
  'wow',
];
// (?<!\*) / (?!\*) keep us from re-wrapping something already adjacent to `**`.
const MARKER_RE = new RegExp(`(?<!\\*)\\b(${COACH_MARKERS.join('|')})\\b(?!\\*)`, 'gi');

function boldMarkers(s: string): string {
  if (!s) return s;
  return s.replace(MARKER_RE, '**$1**');
}

// Wrap a span in `**…**`, keeping any surrounding whitespace outside the bold
// and skipping if the span already contains `**` (so we never nest markers).
function boldSpan(s: string): string {
  const m = s.match(/^(\s*)([\s\S]*?)(\s*)$/);
  const lead = m ? m[1] : '';
  const core = m ? m[2] : s;
  const trail = m ? m[3] : '';
  if (!core || core.includes('**')) return s;
  return `${lead}**${core}**${trail}`;
}

// Index of the first char of the sentence containing `idx` (walks back to the
// previous sentence terminator or newline; 0 if none).
function sentenceStart(text: string, idx: number): number {
  for (let i = idx - 1; i >= 0; i--) {
    const c = text[i];
    if (c === '.' || c === '!' || c === '?' || c === '\n') return i + 1;
  }
  return 0;
}

// Start index of the last paragraph (text after the final blank line), or -1
// when the reply is a single paragraph.
function lastParagraphStart(text: string): number {
  const re = /\n[ \t]*\n/g;
  let last = -1;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = re.exec(text)) !== null) last = m.index + m[0].length;
  return last;
}

// For single-paragraph replies: bold from the start of the trailing run of
// consecutive question sentences. Any non-question lead-in that shares the run
// (no sentence terminator between it and the first question, e.g. a "…:"
// lead-in) is naturally swept in by sentenceStart.
function trailingQuestionRunStart(text: string, lastQ: number): number {
  let start = sentenceStart(text, lastQ);
  // Extend backwards over earlier consecutive question sentences.
  for (;;) {
    let j = start - 1;
    while (j >= 0 && /\s/.test(text[j])) j--;
    if (j >= 0 && text[j] === '?') {
      start = sentenceStart(text, j);
    } else {
      break;
    }
  }
  return start;
}

function emphasizeCoachReply(raw: string): string {
  if (!raw) return raw;

  // 1. Leading praise: a short opening clause ending in "!" ("Exactly!",
  //    "Whoa, nice catch!"). Capped at 40 chars so a long opening sentence
  //    that merely ends with "!" isn't swept up.
  const leadMatch = raw.match(/^(\s*[^\n.!?]{1,40}!)/);
  const leadEnd = leadMatch ? leadMatch[0].length : 0;

  // 2. Closing block.
  let closingStart = raw.length;
  const lastQ = raw.lastIndexOf('?');
  if (lastQ !== -1) {
    const paraStart = lastParagraphStart(raw);
    if (paraStart !== -1 && raw.indexOf('?', paraStart) !== -1) {
      // Multi-paragraph: the final paragraph IS the close — bold all of it
      // (lead-in line + every closing question).
      closingStart = paraStart;
    } else {
      // Single paragraph: bold only the trailing run of questions so the
      // teaching nugget above it stays un-bolded.
      closingStart = trailingQuestionRunStart(raw, lastQ);
    }
  }
  // Guard against the leading praise and closing block overlapping on a tiny
  // reply — drop the closing bold in that case.
  if (closingStart < leadEnd) closingStart = raw.length;

  // Never bold across list items: wrapping ** around lines that start with
  // "1." / "2." / "-" stops markdown from parsing them as a list, so the
  // opening's stacked choices collapse into one run-on bold paragraph.
  // If the closing region contains list markers, skip the closing bold.
  if (closingStart < raw.length && /^\s*(\d+[.)]|[-•])\s/m.test(raw.slice(closingStart))) {
    closingStart = raw.length;
  }

  const leadPart = leadMatch ? boldSpan(raw.slice(0, leadEnd)) : '';
  const middle = raw.slice(leadEnd, closingStart);
  const closingPart = closingStart < raw.length ? boldSpan(raw.slice(closingStart)) : '';

  // Markers run only on the un-bolded middle, so we never nest `**` inside the
  // already-bold leading praise or closing block.
  return `${leadPart}${boldMarkers(middle)}${closingPart}`;
}

// One-time init for mermaid — runs once per page load.
let _mermaidReady = false;
function ensureMermaid() {
  if (_mermaidReady) return;
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    // 'strict' is deliberate: the same markdown pipeline renders STUDENT-typed
    // messages, not just coach replies, so diagram source is untrusted input.
    // 'loose' honours a `click X "javascript:..."` directive — the exact vector
    // someone probed with (localStorage exfil to an ngrok URL). Strict disables
    // click handlers and sanitizes label text; htmlLabels off keeps raw HTML
    // out of the SVG we inject via dangerouslySetInnerHTML. Nothing asks the
    // coach for clickable diagram nodes, so no feature depends on 'loose'.
    securityLevel: 'strict',
    flowchart: { curve: 'basis', htmlLabels: false },
  });
  _mermaidReady = true;
}

// ---------------------------------------------------------------------------
// Zoomable lightbox — generic fullscreen viewer with zoom + pan
// ---------------------------------------------------------------------------
// Body is supplied via `children` so the same harness works for SVG
// (DiagramLightbox) and raster images (ImageLightbox). Kid can scroll-zoom,
// drag to pan, use keyboard (+ − 0), hit Esc to close, or click the backdrop.
const ZoomableLightbox: React.FC<{
  onClose: () => void;
  downloadButtons?: React.ReactNode;
  children: React.ReactNode;
}> = ({ onClose, downloadButtons, children }) => {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') setScale((s) => Math.min(s + 0.25, 6));
      else if (e.key === '-' || e.key === '_') setScale((s) => Math.max(s - 0.25, 0.25));
      else if (e.key === '0') { setScale(1); setPan({ x: 0, y: 0 }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Mouse-wheel zoom (Cmd / Ctrl held = always; otherwise still zoom because
  // this is a modal and the only sensible scroll behaviour is zoom).
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setScale((s) => Math.max(0.25, Math.min(6, s + delta * s)));
  };

  // Click + drag to pan
  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.panY + (e.clientY - dragRef.current.startY),
    });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const reset = () => { setScale(1); setPan({ x: 0, y: 0 }); };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/75 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Stage (the bit that pans + zooms) */}
      <div
        className="relative h-full w-full overflow-hidden"
        style={{ cursor: dragRef.current ? 'grabbing' : 'grab' }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div
          className="pointer-events-none flex h-full w-full items-center justify-center"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transformOrigin: 'center center',
            transition: dragRef.current ? 'none' : 'transform 100ms ease-out',
          }}
        >
          <div
            className="flex items-center justify-center rounded-lg bg-white p-6 shadow-2xl"
            // Cap the rendered body at ~80% of viewport so it fits before zoom.
            // minWidth/minHeight ensure the card is always visible even if
            // the child has zero intrinsic size (SVG with width="100%", a
            // raster that hasn't loaded yet, etc.) — kids can still see
            // and close the modal.
            style={{
              maxWidth: '80vw',
              maxHeight: '80vh',
              minWidth: '320px',
              minHeight: '240px',
            }}
          >
            {children}
          </div>
        </div>
      </div>

      {/* Toolbar — top-right */}
      <div className="absolute right-4 top-4 flex items-center gap-1">
        <button
          type="button"
          onClick={() => setScale((s) => Math.max(0.25, s - 0.25))}
          title="Zoom out (or press −)"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-white/30 bg-white/10 text-white backdrop-blur hover:bg-white/20"
        >
          <RemoveRounded fontSize="small" />
        </button>
        <div className="rounded-md border border-white/30 bg-white/10 px-2 py-1 text-xs font-medium text-white backdrop-blur" style={{ minWidth: 52, textAlign: 'center' }}>
          {Math.round(scale * 100)}%
        </div>
        <button
          type="button"
          onClick={() => setScale((s) => Math.min(6, s + 0.25))}
          title="Zoom in (or press +)"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-white/30 bg-white/10 text-white backdrop-blur hover:bg-white/20"
        >
          <AddRounded fontSize="small" />
        </button>
        <button
          type="button"
          onClick={reset}
          title="Reset zoom (or press 0)"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-white/30 bg-white/10 text-white backdrop-blur hover:bg-white/20"
        >
          <RestartAltRounded fontSize="small" />
        </button>
        {downloadButtons && (
          <>
            <div className="mx-1 h-6 w-px bg-white/30" />
            {downloadButtons}
          </>
        )}
        <div className="mx-1 h-6 w-px bg-white/30" />
        <button
          type="button"
          onClick={onClose}
          title="Close (Esc)"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-white/30 bg-white/10 text-white backdrop-blur hover:bg-white/20"
        >
          <CloseRounded fontSize="small" />
        </button>
      </div>

      {/* Hint footer */}
      <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs text-white/90 backdrop-blur">
        Scroll to zoom · drag to pan · <span className="font-mono">+ − 0</span> on keyboard · Esc to close
      </div>
    </div>
  );
};

// Thin lightbox wrappers — reuse ZoomableLightbox harness with different body
// + different download buttons.

const DiagramLightbox: React.FC<{
  svg: string;
  onClose: () => void;
  onDownloadSvg: () => void;
  onDownloadPng: () => void;
}> = ({ svg, onClose, onDownloadSvg, onDownloadPng }) => {
  const downloadButtons = (
    <>
      <button
        type="button"
        onClick={onDownloadSvg}
        title="Download as SVG"
        className="flex h-9 items-center gap-1 rounded-md border border-white/30 bg-white/10 px-3 text-xs font-medium text-white backdrop-blur hover:bg-white/20"
      >
        <DownloadRounded fontSize="small" /> SVG
      </button>
      <button
        type="button"
        onClick={onDownloadPng}
        title="Download as PNG"
        className="flex h-9 items-center gap-1 rounded-md border border-white/30 bg-white/10 px-3 text-xs font-medium text-white backdrop-blur hover:bg-white/20"
      >
        <DownloadRounded fontSize="small" /> PNG
      </button>
    </>
  );
  return (
    <ZoomableLightbox onClose={onClose} downloadButtons={downloadButtons}>
      <div dangerouslySetInnerHTML={{ __html: svg }} />
    </ZoomableLightbox>
  );
};

const ImageLightbox: React.FC<{
  url: string;
  alt: string;
  onClose: () => void;
}> = ({ url, alt, onClose }) => {
  // Download buttons intentionally removed — generated images stay inline.
  // The lightbox toolbar shows only zoom controls + close.
  return (
    <ZoomableLightbox onClose={onClose}>
      <img
        src={url}
        alt={alt}
        className="block object-contain"
        // Explicit sizing — without this, SVG sources (width="100%") have
        // no intrinsic dimensions and render at 0×0, collapsing the white
        // card to its padding. We force a fixed height of 80vh and let
        // width follow the aspect ratio; PNGs with intrinsic dimensions
        // still respect aspect via `object-contain`.
        style={{
          height: '80vh',
          width: 'auto',
          maxWidth: '80vw',
          minWidth: '320px',
          minHeight: '240px',
        }}
        draggable={false}
      />
    </ZoomableLightbox>
  );
};

// Renders a mermaid diagram. Async-safe: each instance gets its own id so
// streaming partial diagrams don't collide.
//
// Hover reveals a small toolbar with two downloads (SVG, PNG), a copy-source
// button, and a "Zoom" button that opens the diagram fullscreen with pan +
// zoom controls.
// Handwritten "note on paper" from the coach. The model emits a ```handnote
// fenced block; we render its markdown/LaTeX on a lined-paper card in a
// handwriting font so a worked explanation reads like a tutor jotted it down.
// Math still goes through KaTeX (accurate) — KaTeX sets its own font-family, so
// the cursive stack only styles the prose, never the equations.
const HANDWRITING_STACK =
  '"Caveat", "Patrick Hand", "Comic Sans MS", "Segoe Print", "Bradley Hand", cursive';

const HandNote: React.FC<{ content: string }> = ({ content }) => (
  <div
    className="my-3 rounded-lg border border-amber-200 px-5 py-4 shadow-sm"
    style={{
      // Faint cream paper with evenly-spaced ruled lines.
      backgroundColor: '#fffdf5',
      backgroundImage:
        'repeating-linear-gradient(to bottom, transparent 0, transparent 31px, #e8e0c8 31px, #e8e0c8 32px)',
      backgroundPositionY: '8px',
    }}
  >
    <div
      className="handnote-body text-[1.15rem] leading-8 text-slate-800"
      style={{ fontFamily: HANDWRITING_STACK }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[[rehypeKatex, { strict: 'ignore', throwOnError: false, errorColor: '#9CA3AF', output: 'html' }]]}
        components={{
          p({ children }) { return <p className="my-1">{children}</p>; },
          ul({ children }) { return <ul className="my-1 list-disc pl-5">{children}</ul>; },
          ol({ children }) { return <ol className="my-1 list-decimal pl-5">{children}</ol>; },
          li({ children }) { return <li className="my-0.5">{children}</li>; },
          strong({ children }) { return <strong className="font-semibold">{children}</strong>; },
          em({ children }) { return <em className="italic">{children}</em>; },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  </div>
);

const MermaidDiagram: React.FC<{ code: string }> = ({ code }) => {
  const [error, setError] = useState<string | null>(null);
  const [svg, setSvg] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const idRef = useRef(`m${Math.floor(Math.random() * 1e9)}`);

  useEffect(() => {
    ensureMermaid();
    let cancelled = false;
    (async () => {
      try {
        await mermaid.parse(code);
        const { svg: rendered } = await mermaid.render(idRef.current, code);
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'diagram error');
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  // Pick a friendly filename seed from the first node label inside the diagram
  // source — falls back to "diagram" if nothing readable is found.
  const baseName = useMemo(() => {
    const labelMatch = code.match(/\[([^\]]{3,60})\]/);
    return `curiosity-${slugForFilename(labelMatch ? labelMatch[1] : 'diagram')}`;
  }, [code]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* no-op — clipboard access can fail on insecure contexts */
    }
  };

  // Memoised callbacks so the lightbox doesn't re-render when state changes
  const handleDownloadSvg = useCallback(() => downloadSvg(svg, baseName), [svg, baseName]);
  const handleDownloadPng = useCallback(() => { downloadPng(svg, baseName).catch(() => {}); }, [svg, baseName]);

  if (error && !svg) {
    return <div className="text-xs italic text-slate-400 my-2">drawing…</div>;
  }

  return (
    <>
      <div className="group relative my-2 overflow-x-auto rounded-lg border border-violet-100 bg-white p-3">
        {/* The diagram itself — click anywhere on it to enlarge */}
        <div
          className="mermaid-container cursor-zoom-in"
          onClick={() => setLightboxOpen(true)}
          title="Click to enlarge"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
        {/* Toolbar — invisible until hover, focus, or touch */}
        <div className="pointer-events-none absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-150 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
            title="Enlarge (zoom + pan)"
            className="flex items-center gap-1 rounded-md border border-violet-200 bg-white/95 px-2 py-1 text-[11px] font-medium text-violet-700 shadow-sm hover:bg-violet-50"
          >
            <ZoomInRounded fontSize="inherit" /> Enlarge
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDownloadSvg(); }}
            title="Download as SVG (sharp at any size)"
            className="flex items-center gap-1 rounded-md border border-violet-200 bg-white/95 px-2 py-1 text-[11px] font-medium text-violet-700 shadow-sm hover:bg-violet-50"
          >
            <DownloadRounded fontSize="inherit" /> SVG
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleDownloadPng(); }}
            title="Download as PNG (paste into Word / Google Docs)"
            className="flex items-center gap-1 rounded-md border border-violet-200 bg-white/95 px-2 py-1 text-[11px] font-medium text-violet-700 shadow-sm hover:bg-violet-50"
          >
            <DownloadRounded fontSize="inherit" /> PNG
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleCopy(); }}
            title={copied ? 'Copied!' : 'Copy diagram source'}
            className="flex items-center gap-1 rounded-md border border-violet-200 bg-white/95 px-2 py-1 text-[11px] font-medium text-violet-700 shadow-sm hover:bg-violet-50"
          >
            {copied ? <CheckRounded fontSize="inherit" /> : <ContentCopyRounded fontSize="inherit" />}
            {copied ? 'Copied' : 'Source'}
          </button>
        </div>
      </div>

      {lightboxOpen && (
        <DiagramLightbox
          svg={svg}
          onClose={() => setLightboxOpen(false)}
          onDownloadSvg={handleDownloadSvg}
          onDownloadPng={handleDownloadPng}
        />
      )}
    </>
  );
};

// Wraps a KaTeX-rendered display equation so the student can copy the LaTeX
// source. The display block itself is still rendered by rehype-katex; we just
// inject a tiny hover toolbar in the same group container.
//
// LaTeX source is recovered at click-time from KaTeX's own <annotation> child
// — that's the stable contract KaTeX guarantees regardless of plugin version.
const EquationBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    const ann = wrapRef.current?.querySelector('annotation[encoding="application/x-tex"]');
    const source = (ann?.textContent || '').trim();
    if (!source) return;
    try {
      await navigator.clipboard.writeText(source);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* no-op */ }
  };
  return (
    <div ref={wrapRef} className="group relative">
      {children}
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy LaTeX source'}
        className="absolute right-1 top-1 flex items-center gap-1 rounded-md border border-violet-200 bg-white/95 px-2 py-0.5 text-[10px] font-medium text-violet-700 opacity-0 shadow-sm transition-opacity duration-150 hover:bg-violet-50 group-hover:opacity-100"
      >
        {copied ? <CheckRounded fontSize="inherit" /> : <ContentCopyRounded fontSize="inherit" />}
        {copied ? 'Copied' : 'LaTeX'}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// GeneratedImageBlock — inline AI-generated images with pending-state polling
// ---------------------------------------------------------------------------
// A markdown `![](url)` whose `url` matches `/api/images/img_*` is rendered
// here instead of a bare `<img>`. Behaviour:
//
//   * On mount, HEAD-poll the URL. While the server returns 202 (pending),
//     show a soft skeleton card with the caption underneath ("drawing your
//     picture…"). Once the server returns 200, swap to the real <img>.
//   * After ~30s of polling without success, give up and show a small
//     "couldn't generate" line — kids shouldn't see broken-image icons.
//   * On hover (when ready), reveal Enlarge + Download. Click anywhere on
//     the image to open ImageLightbox with zoom/pan.
//
// Non-API images (anything that doesn't match the prefix) bypass polling
// entirely and render as a normal <img>.
const GENERATED_IMAGE_URL_RE = /\/api\/images\/img_[a-z0-9]+/i;

/**
 * Resolve a possibly-relative `/api/images/...` URL to the backend origin.
 * The backend emits relative URLs (so saved message content survives
 * a backend host change), but the frontend dev server is on a different
 * port — without this, the browser fetches from the frontend origin and
 * gets the SPA shell instead of the image.
 *
 * In production where frontend + backend share an origin, the env var
 * is "" so the URL stays relative — exactly what we want.
 */
function resolveImageUrl(src: string): string {
  // Already absolute → use as-is.
  if (/^https?:\/\//i.test(src)) return src;
  // Not one of our managed image URLs → no rewrite, render as-is.
  if (!GENERATED_IMAGE_URL_RE.test(src)) return src;
  const base = (process.env.REACT_APP_BACKEND_BASE_URL || '').replace(/\/+$/, '');
  return base ? `${base}${src.startsWith('/') ? '' : '/'}${src}` : src;
}

export const GeneratedImageBlock: React.FC<{ src: string; alt?: string }> = ({ src, alt = '' }) => {
  // Resolved URL the <img> / poller will actually hit.
  const resolvedSrc = useMemo(() => resolveImageUrl(src), [src]);
  const isManaged = GENERATED_IMAGE_URL_RE.test(src);

  // ---------------------------------------------------------------------
  // Strategy: IndexedDB-first, network second.
  //
  // On mount, check IDB for the cached blob. If hit → serve from local
  // storage via an object URL (zero network). If miss → fetch once,
  // store the blob in IDB, then render. Subsequent opens of any
  // conversation containing the same image are pure local reads.
  //
  // Pending-state fallback (server returns 202 mid-generation) still
  // kicks in: an <img> error or a fetch-returned 202 triggers the
  // polling effect. When polling resolves to ready, the network fetch
  // runs once and populates IDB.
  // ---------------------------------------------------------------------
  const [status, setStatus] = useState<'ready' | 'pending' | 'failed'>('ready');
  const [retryKey, setRetryKey] = useState(0);
  const [cachedObjectUrl, setCachedObjectUrl] = useState<string | null>(null);
  // Keep the raw Blob in a ref so download handlers can use the bytes
  // directly instead of re-fetching (which would need CORS + a live URL).
  // The Blob keeps itself alive as long as the ref holds it, even if the
  // object URL gets revoked during a remount.
  const cachedBlobRef = useRef<Blob | null>(null);
  const hasLoadedRef = useRef(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // -------------------------------------------------------------------
  // Local cache lookup + network-fetch-with-cache-store, runs on mount
  // for every managed image. Non-managed images skip this entirely.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!isManaged) return;
    let cancelled = false;
    let createdObjectUrl: string | null = null;

    (async () => {
      // 1. IDB hit → render from local blob, no network.
      const cached = await getCachedImageBlob(resolvedSrc);
      if (cancelled) return;
      if (cached) {
        cachedBlobRef.current = cached;
        createdObjectUrl = URL.createObjectURL(cached);
        setCachedObjectUrl(createdObjectUrl);
        return;
      }

      // 2. IDB miss → fetch once, persist, render via object URL.
      let resp: Response;
      try {
        resp = await fetch(resolvedSrc);
      } catch {
        // Network down — leave the <img> to try directly (it'll error,
        // polling will pick up).
        return;
      }
      if (cancelled) return;

      if (resp.status === 202) {
        // Still generating. Hand off to the polling flow.
        setStatus('pending');
        return;
      }
      if (resp.status === 404) {
        setStatus('failed');
        return;
      }
      if (!resp.ok) return;

      const blob = await resp.blob();
      if (cancelled) return;

      // Fire-and-forget cache write.
      putCachedImageBlob(resolvedSrc, blob);

      cachedBlobRef.current = blob;
      createdObjectUrl = URL.createObjectURL(blob);
      setCachedObjectUrl(createdObjectUrl);
    })();

    return () => {
      cancelled = true;
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
    };
  }, [resolvedSrc, isManaged]);

  // The URL the <img> tag and download buttons actually use. Prefer the
  // cached blob URL when we have it (zero network on every subsequent
  // access including downloads). Fallback to the resolved network URL
  // for the brief window before the cache lookup finishes, and for
  // non-managed images.
  const displaySrc = cachedObjectUrl || resolvedSrc;

  // Pending-state polling — only runs after an <img> error switched us in.
  useEffect(() => {
    if (status !== 'pending') return;
    let attempts = 0;
    // Backend gives the image API up to IMAGE_GEN_TIMEOUT_SEC=60s (visuals.py)
    // before failing. Add buffer for network + storage write + status update,
    // so the user doesn't see "Image couldn't be generated" for a row the
    // backend is still legitimately working on (then have to reload to see
    // the image suddenly appear). 60 × 1.5s = 90s of HEAD polling — cheap.
    const MAX_ATTEMPTS = 60;            // ~90s at 1.5s interval
    const POLL_MS = 1500;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const poll = async () => {
      attempts += 1;
      try {
        const resp = await fetch(resolvedSrc, { method: 'HEAD' });
        if (cancelled) return;
        if (resp.status === 200) {
          // Bump retry key so the next <img> instance refetches (the
          // previous one had a stale 202 cached in browser memory).
          setRetryKey((k) => k + 1);
          setStatus('ready');
          return;
        }
        if (resp.status === 404) {
          setStatus('failed');
          return;
        }
        // 202 or any other "not yet ready" — keep waiting.
      } catch {
        // Network blip — try again until attempts run out.
      }
      if (attempts >= MAX_ATTEMPTS) {
        if (!cancelled) setStatus('failed');
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [resolvedSrc, status]);

  const handleImgError = () => {
    if (!isManaged) return;
    // If the image had already loaded successfully once, treat a later
    // error as a transient blip — don't restart polling.
    if (hasLoadedRef.current) return;
    setStatus('pending');
  };
  const handleImgLoad = () => {
    hasLoadedRef.current = true;
  };

  if (status === 'pending') {
    return (
      <span className="my-3 flex flex-col items-stretch gap-2 rounded-lg border border-violet-100 bg-violet-50/40 p-4">
        <span className="flex items-center gap-3">
          <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-violet-300 border-t-violet-700" />
          <span className="text-sm font-medium text-violet-700">Drawing your picture…</span>
        </span>
        {alt ? (
          <span className="text-xs italic text-slate-500">{alt}</span>
        ) : null}
        <span className="h-32 w-full animate-pulse rounded-md bg-violet-100/70" />
      </span>
    );
  }

  if (status === 'failed') {
    return (
      <span className="my-3 inline-flex flex-col items-start gap-1 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs">
        <span className="font-medium text-amber-800">Image couldn't be generated.</span>
        {alt ? <span className="italic text-amber-700">{alt}</span> : null}
      </span>
    );
  }

  // status === 'ready' — render the image with hover toolbar + click-to-zoom.
  // `key` busts the <img> identity when polling decides to retry, forcing a
  // fresh GET (the previous response may have been a 202).
  return (
    <>
      <span
        className="group relative my-3 inline-block max-w-full overflow-hidden rounded-lg border border-violet-100 bg-white shadow-sm"
        style={{ isolation: 'isolate' }}
      >
        <img
          key={retryKey}
          src={displaySrc}
          alt={alt}
          onClick={() => setLightboxOpen(true)}
          onLoad={handleImgLoad}
          onError={handleImgError}
          className="block max-w-full cursor-zoom-in"
          title={alt || 'Click to enlarge'}
          style={{ position: 'relative', zIndex: 1 }}
        />
        {alt ? (
          <span className="block px-3 py-1.5 text-[11px] italic text-slate-500">{alt}</span>
        ) : null}
        {/* Toolbar — Enlarge only.
            Download buttons removed per product decision: generated images
            stay inline in the chat. (Mermaid diagrams keep their downloads
            via the separate MermaidDiagram component — different code path,
            different intent: those are diagrams the kid might paste into a
            doc, while generated images are just visual aids for the chat.)
            Still always-clickable + always partially visible so touch users
            can spot the button without hovering. */}
        <span
          className="pointer-events-auto absolute right-2 top-2 z-10 flex gap-1 opacity-70 transition-opacity duration-150 group-hover:opacity-100"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
            title="Enlarge (zoom + pan)"
            className="flex items-center gap-1 rounded-md border border-violet-200 bg-white/95 px-2 py-1 text-[11px] font-medium text-violet-700 shadow-sm hover:bg-violet-50"
          >
            <ZoomInRounded fontSize="inherit" /> Enlarge
          </button>
        </span>
      </span>
      {lightboxOpen && (
        <ImageLightbox
          url={displaySrc}
          alt={alt}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Read-aloud (TTS)
// ---------------------------------------------------------------------------

// Only one message may play at a time, chat-wide. The module-level slot holds
// the playing <audio> plus a callback that flips its owner button back to
// idle — starting a new playback (or clicking stop) silences the previous one.
let activeTtsAudio: { audio: HTMLAudioElement; reset: () => void } | null = null;

function stopActiveTtsAudio() {
  if (activeTtsAudio) {
    activeTtsAudio.audio.pause();
    activeTtsAudio.reset();
    activeTtsAudio = null;
  }
}

type TtsState = 'idle' | 'loading' | 'playing';

/**
 * Speaker button that reads an AI reply aloud. Fetches the MP3 from
 * /api/speech/tts/<id> on first click (the backend caches it, so replays are
 * instant) and keeps the object URL for the component's lifetime.
 */
const ListenButton: React.FC<{ messageId: number }> = ({ messageId }) => {
  const [state, setState] = useState<TtsState>('idle');
  const urlRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      // Unmount: stop our own playback and free the blob URL.
      if (audioRef.current) {
        audioRef.current.pause();
        if (activeTtsAudio?.audio === audioRef.current) activeTtsAudio = null;
      }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  const handleClick = async () => {
    if (state === 'loading') return;
    if (state === 'playing') {
      stopActiveTtsAudio(); // our reset callback flips us back to idle
      return;
    }
    stopActiveTtsAudio();
    setState('loading');
    try {
      if (!urlRef.current) {
        const blob = await fetchMessageAudio(messageId);
        urlRef.current = URL.createObjectURL(blob);
      }
      const audio = new Audio(urlRef.current);
      audioRef.current = audio;
      const reset = () => setState('idle');
      const release = () => {
        if (activeTtsAudio?.audio === audio) activeTtsAudio = null;
        reset();
      };
      audio.onended = release;
      audio.onerror = release;
      activeTtsAudio = { audio, reset };
      await audio.play();
      setState('playing');
    } catch (err) {
      // 422 = nothing speakable (diagram-only reply); anything else is a
      // transient failure. Either way the button just returns to idle.
      console.error('Read-aloud failed:', err);
      setState('idle');
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={state === 'loading'}
      title={state === 'playing' ? 'Stop' : 'Listen'}
      aria-label={state === 'playing' ? 'Stop reading aloud' : 'Read this reply aloud'}
      className="flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-violet-500 transition-colors hover:bg-violet-100 hover:text-violet-700 disabled:cursor-wait"
    >
      {state === 'loading' ? (
        <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
      ) : state === 'playing' ? (
        <StopRounded sx={{ fontSize: 16 }} />
      ) : (
        <VolumeUpRounded sx={{ fontSize: 16 }} />
      )}
      {state === 'playing' ? 'Stop' : 'Listen'}
    </button>
  );
};

interface ChatMessageProps {
  message: Message;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const { content, is_user, status } = message;
  const [ellipsis, setEllipsis] = useState('.');
  const [isVisible, setIsVisible] = useState(false);

  // Sanitise common LLM math mistakes (orphan \begin{aligned}, naked \frac
  // lines, etc.) before handing to the markdown parser. Memoised separately
  // so we don't re-run it on unrelated re-renders.
  const sanitised = useMemo(() => {
    // Coach replies get the Socratic question + "wow" markers bolded; the
    // kid's own messages are left exactly as typed.
    const emphasised = is_user ? content : emphasizeCoachReply(content);
    return sanitizeMath(emphasised);
  }, [content, is_user]);

  // Markdown renderer config — pure data, memoised.
  const markdownContent = useMemo(() => {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[
          [rehypeKatex, {
            // Render bad LaTeX as the original source instead of red error
            // text. Prevents one mistyped equation from breaking the whole
            // reply visually.
            strict: 'ignore',
            throwOnError: false,
            errorColor: '#9CA3AF',
            output: 'html',
          }],
        ]}
        components={{
          code({ inline, className, children, ...props }: any) {
            const text = String(children).replace(/\n$/, '');
            const lang = /language-(\w+)/.exec(className || '')?.[1];
            // react-markdown v9+ no longer passes `inline`, so `!inline` was
            // true for EVERY code span — single-backtick tokens like
            // `36 -> 49 -> 64` rendered as full-width dark blocks (the "black
            // thing" TVG kept flagging). Detect inline ourselves: no newline
            // and no language tag = an inline span.
            const isInline = inline ?? (!/\n/.test(text) && !lang);

            // Mermaid block → diagram. Proper case: triple-backtick fence
            // with `mermaid` language tag set by the markdown parser.
            if (!inline && lang === 'mermaid') {
              return <MermaidDiagram code={text} />;
            }
            // Defensive fallback: the LLM sometimes emits the Mermaid block
            // with single backticks instead of triple (or omits the language
            // tag), so the content arrives as "mermaid\ngraph TD\n...". Catch
            // that shape and route to the diagram renderer regardless of
            // whether the parser tagged it inline or block.
            if (/^mermaid\s*\n/i.test(text)) {
              return <MermaidDiagram code={text.replace(/^mermaid\s*\n/i, '')} />;
            }
            // Handwritten note → paper card in a handwriting font (with KaTeX math).
            if (!inline && lang === 'handnote') {
              return <HandNote content={text} />;
            }
            // Defensive fallback for a single-backtick / untagged handnote block.
            if (/^handnote\s*\n/i.test(text)) {
              return <HandNote content={text.replace(/^handnote\s*\n/i, '')} />;
            }
            // Inline code (no language tag) → styled span
            if (isInline) {
              return <code className="rounded bg-violet-50 px-1 py-0.5 text-[0.9em] text-slate-800" {...props}>{children}</code>;
            }
            // Fenced code block — light card, never a black terminal slab
            // (TVG: no black-and-white blocks in explanations, math or otherwise).
            return (
              <pre className="my-2 overflow-x-auto rounded-md border border-violet-100 bg-violet-50 p-3 text-xs text-slate-800">
                <code className={className} {...props}>{children}</code>
              </pre>
            );
          },
          p({ children }) { return <p className="my-1">{children}</p>; },
          // ![](url) — for /api/images/img_* this swaps in our pending-aware
          // block with hover toolbar and click-to-zoom. Other URLs fall back
          // to a normal <img>.
          img({ src, alt }: any) {
            return <GeneratedImageBlock src={String(src || '')} alt={alt || ''} />;
          },
          ul({ children }) { return <ul className="my-1 list-disc pl-5">{children}</ul>; },
          ol({ children }) { return <ol className="my-1 list-decimal pl-5">{children}</ol>; },
          li({ children }) { return <li className="my-0.5">{children}</li>; },
          strong({ children }) { return <strong className="font-semibold">{children}</strong>; },
          em({ children }) { return <em className="italic">{children}</em>; },
          a({ children, href }) {
            return <a href={href} target="_blank" rel="noopener noreferrer" className="text-violet-700 underline">{children}</a>;
          },
          // rehype-katex emits display-mode equations as
          // <div class="math math-display">…</div>. Wrap that container in
          // EquationBlock so the student can copy the LaTeX source on hover.
          // The LaTeX is recovered from KaTeX's own <annotation> child at
          // click time — no plugin-version-specific data plumbing needed.
          div({ className, children, ...props }: any) {
            if (className && /math-display/.test(className)) {
              return (
                <EquationBlock>
                  <div className={className} {...props}>{children}</div>
                </EquationBlock>
              );
            }
            return <div className={className} {...props}>{children}</div>;
          },
        }}
      >
        {sanitised}
      </ReactMarkdown>
    );
  }, [sanitised]);

  useEffect(() => {
    setIsVisible(true);
    let interval: NodeJS.Timeout | null = null;
    if (status === 'sending') {
      interval = setInterval(() => {
        setEllipsis(prev => prev === '...' ? '.' : prev + '.');
      }, 500);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [status]);

  const alignment = is_user ? 'justify-end' : 'justify-start';
  const surfaceClasses = is_user
    ? 'bg-violet-600 text-white'
    : 'border border-violet-200 bg-[#faf7ff] text-slate-800';
  const opacity = status === 'sending' ? 'opacity-70' : 'opacity-100';
  const errorStyle = status === 'error' ? 'border border-red-500' : '';
  const hoverEffect = 'transition-all duration-200';
  const appearAnimation = isVisible
    ? 'translate-y-0 opacity-100'
    : 'translate-y-2 opacity-0';
  const animationTiming = is_user
    ? 'transition-all duration-300 ease-out'
    : 'transition-all duration-300 ease-out delay-100';

  return (
    <>
    <div className={`flex ${alignment} ${opacity} px-4 sm:px-6 lg:px-8 ${animationTiming} ${appearAnimation}`}>
      {!is_user && (
        <div className="mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-violet-100">
          <AutoAwesomeRounded fontSize="small" className="text-violet-700" />
        </div>
      )}

      <div className={`min-w-[120px] max-w-[75%] break-words rounded-2xl px-3 py-2 shadow-sm sm:max-w-md sm:px-4 ${surfaceClasses} ${errorStyle} ${hoverEffect} lg:max-w-lg`}>
        {/* prose-invert on the kid's bubble: the violet-600 background needs
            light text, but the typography plugin otherwise forces its dark
            body colour onto the markdown, leaving near-black text that's
            unreadable on purple. */}
        <div className={`prose prose-sm max-w-none prose-p:my-1 ${is_user ? 'prose-invert' : ''}`}>
          {markdownContent}
        </div>
        {/* Read-aloud: AI replies only, and only once the stream is done and
            the real DB message id exists (temp streaming bubbles have no id). */}
        {!is_user && typeof message.id === 'number' && status !== 'sending' && (
          <div className="mt-1 flex justify-end">
            <ListenButton messageId={message.id} />
          </div>
        )}
        {status === 'sending' && (
          <span className="text-xs italic ml-2 block opacity-80">
            sending{ellipsis}
          </span>
        )}
        {status === 'error' && (
          <span className="text-xs italic ml-2 block text-red-300">
            Failed to send
          </span>
        )}
      </div>
    </div>
    {!is_user && typeof message.id === 'number' && (
      <div className="px-4 sm:px-6 lg:px-8">
        <IntentRouterPanel messageId={message.id} />
      </div>
    )}
    </>
  );
};

export default ChatMessage;
